import React, { useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { CaretDownIcon, CheckIcon, XIcon } from "@phosphor-icons/react";

import CustomText from "../../../common/general/CustomText/CustomText";
import type { McqAnswerDetail } from "@axio/graphql/generated/graphql";
import { colors, spacing, radii } from "../../../../styles/design-system";

interface McqQuestionCardProps {
  mcqAnswer: McqAnswerDetail;
  questionNumber: string;
}

type QuestionStatus = "correct" | "incorrect" | "partial";

const CHOICE_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

const getQuestionStatus = (mcqAnswer: McqAnswerDetail): QuestionStatus => {
  if (mcqAnswer.isCorrect) return "correct";
  const total = mcqAnswer.totalScore ?? 1;
  if (mcqAnswer.pointsAwarded > 0 && mcqAnswer.pointsAwarded < total) return "partial";
  return "incorrect";
};

const STATUS_CONFIG: Record<QuestionStatus, { label: string; color: string; icon: "check" | "x" | null }> = {
  correct: { label: "Correct", color: colors.success[600], icon: "check" },
  incorrect: { label: "Incorrect", color: colors.error[600], icon: "x" },
  partial: { label: "Partial", color: colors.warning[600], icon: null },
};

const McqQuestionCard: React.FC<McqQuestionCardProps> = ({ mcqAnswer, questionNumber }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const status = getQuestionStatus(mcqAnswer);
  const config = STATUS_CONFIG[status];
  const isMultipleCorrect = mcqAnswer.correctChoiceIndices.length > 1;
  const selectedChoiceSet = new Set(mcqAnswer.selectedChoiceIndices ?? []);

  return (
    <View style={[styles.card, status === "incorrect" && styles.cardIncorrect]}>
      <Pressable style={styles.header} onPress={() => setIsExpanded(!isExpanded)}>
        <View style={styles.headerLeft}>
          <View style={styles.questionNumberRow}>
            <CustomText
              text={questionNumber}
              size="xs"
              weight="bold"
              style={styles.questionNumber}
              useTranslationText={false}
            />
            {isMultipleCorrect && (
              <CustomText
                text="(Select all that apply)"
                size="xs"
                style={styles.multiSelectHint}
                useTranslationText={false}
              />
            )}
          </View>
          <CustomText text={mcqAnswer.question} size="s" style={styles.questionText} useTranslationText={false} />
        </View>
        <View style={styles.headerRight}>
          <View style={styles.statusBadge}>
            {config.icon === "check" && <CheckIcon size={14} color={config.color} weight="bold" />}
            {config.icon === "x" && <XIcon size={14} color={config.color} weight="bold" />}
            <CustomText
              text={config.label}
              size="xs"
              weight="bold"
              style={{ color: config.color }}
              useTranslationText={false}
            />
          </View>
          <CaretDownIcon
            size={16}
            color={colors.grey[400]}
            style={isExpanded ? { transform: "rotate(180deg)" } : undefined}
          />
        </View>
      </Pressable>

      {isExpanded && (
        <View style={styles.choicesContainer}>
          {(mcqAnswer.choices ?? []).map((choice, index) => {
            const isSelected = selectedChoiceSet.has(index);
            const isCorrectChoice = mcqAnswer.correctChoiceIndices.includes(index);
            const isSelectedCorrect = isSelected && isCorrectChoice;
            const isSelectedIncorrect = isSelected && !isCorrectChoice;
            const showAsCorrectHint = !isSelected && isCorrectChoice && !mcqAnswer.isCorrect;

            return (
              <View
                key={index}
                style={[
                  styles.choiceRow,
                  isSelectedCorrect && styles.choiceRowCorrect,
                  isSelectedIncorrect && styles.choiceRowIncorrect,
                  showAsCorrectHint && styles.choiceRowCorrect,
                ]}>
                <View
                  style={[styles.choiceLetter, (isSelectedCorrect || showAsCorrectHint) && styles.choiceLetterCorrect]}>
                  <CustomText
                    text={CHOICE_LETTERS[index] ?? `${index + 1}`}
                    size="xs"
                    weight="bold"
                    style={[
                      styles.choiceLetterText,
                      (isSelectedCorrect || showAsCorrectHint) && styles.choiceLetterTextWhite,
                    ]}
                    useTranslationText={false}
                  />
                </View>
                <CustomText
                  text={choice}
                  size="s"
                  weight={isSelectedCorrect || showAsCorrectHint ? "bold" : "normal"}
                  style={styles.choiceText}
                  useTranslationText={false}
                />
                {(isSelectedCorrect || showAsCorrectHint) && (
                  <CheckIcon size={18} color={colors.success[600]} weight="bold" />
                )}
                {isSelectedIncorrect && <XIcon size={18} color={colors.error[600]} weight="bold" />}
              </View>
            );
          })}

          {mcqAnswer.explanation && !mcqAnswer.isCorrect && (
            <View style={styles.explanationContainer}>
              <CustomText
                text="Explanation"
                size="xs"
                weight="bold"
                style={styles.explanationLabel}
                useTranslationText={false}
              />
              <CustomText
                text={mcqAnswer.explanation}
                size="s"
                style={styles.explanationText}
                useTranslationText={false}
              />
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    backgroundColor: colors.white,
    overflow: "hidden",
  },
  cardIncorrect: {
    borderColor: colors.brand[300],
    borderWidth: 1.5,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.xs,
  },
  headerLeft: {
    flex: 1,
    gap: spacing.xs,
  },
  questionNumberRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  questionNumber: {
    color: colors.brand[500],
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  multiSelectHint: {
    color: colors.grey[500],
    fontStyle: "italic",
  },
  questionText: {
    color: colors.grey[900],
    lineHeight: 22,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    flexShrink: 0,
    paddingTop: 2,
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  choicesContainer: {
    paddingHorizontal: spacing.sm,
    paddingBottom: spacing.sm,
    gap: spacing.xs,
  },
  choiceRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: colors.grey[50],
    gap: spacing.sm,
  },
  choiceRowCorrect: {
    backgroundColor: colors.success[50],
  },
  choiceRowIncorrect: {
    backgroundColor: colors.error[50],
  },
  choiceLetter: {
    width: 28,
    height: 28,
    borderRadius: radii.full,
    backgroundColor: colors.grey[200],
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  choiceLetterCorrect: {
    backgroundColor: colors.success[600],
  },
  choiceLetterText: {
    color: colors.grey[700],
  },
  choiceLetterTextWhite: {
    color: colors.white,
  },
  choiceText: {
    flex: 1,
    color: colors.grey[700],
    lineHeight: 20,
  },
  explanationContainer: {
    marginTop: spacing.xs,
    padding: 14,
    backgroundColor: colors.warning[25],
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.warning[200],
    gap: spacing.xxs,
  },
  explanationLabel: {
    color: colors.warning[700],
  },
  explanationText: {
    color: colors.warning[800],
    lineHeight: 20,
  },
});

export default McqQuestionCard;
